import { useMutation, useQueryClient } from '@tanstack/react-query'
import { produce } from 'immer'
import { contentVersionAnnotations } from '../../v3/contentVersionAnnotations'
import type {
  AnnotationLayout,
  ReadContentVersionAnnotationsDocument,
} from '../../v3/contentVersionAnnotations/types'
import { globalQueryKeys } from '../queryKeys'

type CreateParams = {
  x: number
  y: number
  layout: AnnotationLayout
  comment: string
}

/**
 * Creates a pin annotation with an optimistic append to the cached collection,
 * rolling back on error. The optimistic write targets the raw JSON:API document the
 * query caches; the read hook's `select` re-derives the flattened list from it.
 *
 * On settle it also invalidates the content_versions collections so rail annotation
 * counts refresh. This hook only knows the version, not its parent content, so it
 * matches the `content_versions` key prefix rather than a single content's key.
 */
export const useCreateContentVersionAnnotation = (versionUuid: string) => {
  const queryClient = useQueryClient()
  const queryKey = globalQueryKeys.contentVersionAnnotations(versionUuid)

  return useMutation({
    mutationFn: ({ x, y, layout, comment }: CreateParams) =>
      contentVersionAnnotations.create({ versionUuid, x, y, layout, comment }),
    onMutate: async ({ x, y, layout, comment }: CreateParams) => {
      await queryClient.cancelQueries(queryKey)
      const previous =
        queryClient.getQueryData<ReadContentVersionAnnotationsDocument>(
          queryKey
        )

      queryClient.setQueryData<ReadContentVersionAnnotationsDocument>(
        queryKey,
        (document) =>
          produce(document, (draft) => {
            if (!draft) return
            // The wire type has a non-null author (the server always sets one),
            // but the optimistic row has none until the settle refetch replaces
            // it; the selector renders missing authors as "Unknown".
            draft.data.push({
              id: `optimistic-${Date.now()}`,
              type: 'content_version_annotation',
              attributes: {
                x,
                y,
                layout,
                comment,
                created_at: new Date().toISOString(),
              },
              relationships: {
                author: { data: null },
                content_version: {
                  data: { id: versionUuid, type: 'content_version' },
                },
              },
            } as unknown as (typeof draft.data)[number])
          })
      )

      return { previous }
    },
    onError: (_error, _params, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries(queryKey)
      queryClient.invalidateQueries(globalQueryKeys.allContentVersions())
    },
  })
}
